import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

// FIG-438 rule 5: MPN joins the fixed list, since it is what the PO reader
// gives a code when the document had no column header.
const IDENTIFIER_TYPE_OPTIONS = [
  "SKU",
  "ASIN",
  "EAN",
  "GTIN",
  "UPC",
  "Supplier Code",
  "MPN",
] as const;

export interface CreateProductDialogResult {
  /** The type as shown and stored on the legacy product and mapping rows. */
  identifierType: string;
  /**
   * The same type in the spelling Variant Mapping already knows it by.
   *
   * `commit_variant_mapping_decision` finds the code's `identifier_observation`
   * by an exact, case-sensitive `interpreted_identifier_type`, and raises when
   * there is none. The PO reader stores the printed column header as observed,
   * so a document headed `sku` is observed as `sku` -- canonicalising that to
   * `SKU` for the Select and then sending `SKU` matched no observation and the
   * Variant write failed, after the legacy rows had already been saved.
   *
   * So when the admin keeps the PO code's own type, this carries the header's
   * spelling rather than the list's. It differs from `identifierType` only by
   * case, and only then.
   */
  variantIdentifierType: string;
  primaryValue: string | null;
  description: string | null;
}

interface CreateProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  extractedIdentifier: string;
  extractedDescription: string | null;
  orgProductIdentifier: string;
  /**
   * FIG-438 rule 5: the type the PO reader gave this code -- the printed column
   * header, or MPN. It is pre-selected, and offered in the list when it is not
   * one of the fixed options.
   */
  poCodeType?: string;
  onConfirm: (result: CreateProductDialogResult) => void;
  isCreating: boolean;
}

export function CreateProductDialog({
  open,
  onOpenChange,
  extractedIdentifier,
  extractedDescription,
  orgProductIdentifier,
  poCodeType,
  onConfirm,
  isCreating,
}: CreateProductDialogProps) {
  const trimmedPoCodeType = poCodeType?.trim() ?? "";
  // A header that matches a fixed type apart from case is that type. Keeping the
  // header's own spelling would leave the Select holding a value no item has, so
  // it would render blank and submit a type the admin never saw.
  const canonicalPoCodeType = trimmedPoCodeType
    ? IDENTIFIER_TYPE_OPTIONS.find(
        (option) => option.toLowerCase() === trimmedPoCodeType.toLowerCase(),
      ) ?? trimmedPoCodeType
    : "";
  // The code's own type, not the organisation's primary: a code read under
  // "PART NUMBER" is a part number until someone says otherwise.
  const defaultIdentifierType = canonicalPoCodeType || orgProductIdentifier;
  const typeOptions = useMemo(() => {
    const options: string[] = [...IDENTIFIER_TYPE_OPTIONS];
    // Compared without regard to case, so a header reading "sku" does not
    // produce a second SKU entry.
    if (
      trimmedPoCodeType
      && !options.some((option) => option.toLowerCase() === trimmedPoCodeType.toLowerCase())
    ) {
      options.push(trimmedPoCodeType);
    }
    return options;
  }, [trimmedPoCodeType]);

  const [identifierType, setIdentifierType] = useState(defaultIdentifierType);
  const [primaryValue, setPrimaryValue] = useState("");
  const [description, setDescription] = useState(extractedDescription ?? "");

  // Reset state when dialog opens with new data
  useEffect(() => {
    if (open) {
      setIdentifierType(defaultIdentifierType);
      setPrimaryValue("");
      setDescription(extractedDescription ?? "");
    }
  }, [open, defaultIdentifierType, extractedDescription]);

  const typeMatchesOrg = identifierType === orgProductIdentifier;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Only the pre-selected PO type was re-cased on the way in, so only it is
    // put back. A type the admin picked from the list is sent as the list spells it.
    const keptPoCodeType = identifierType === canonicalPoCodeType && trimmedPoCodeType !== "";
    onConfirm({
      identifierType,
      variantIdentifierType: keptPoCodeType ? trimmedPoCodeType : identifierType,
      primaryValue: typeMatchesOrg ? null : primaryValue.trim() || null,
      description: description.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isCreating) onOpenChange(v); }}>
      <DialogContent
        className="sm:max-w-md"
        onEscapeKeyDown={(e) => { if (isCreating) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isCreating) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Create New Product</DialogTitle>
          <DialogDescription>
            Creating a product for identifier{" "}
            <span className="font-mono font-semibold">{extractedIdentifier}</span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="identifier-type">
              What type of identifier is this?
            </Label>
            <Select value={identifierType} onValueChange={setIdentifierType}>
              <SelectTrigger id="identifier-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {typeOptions.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                    {opt === orgProductIdentifier ? " (primary)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!typeMatchesOrg && (
            <div className="space-y-2">
              <Label htmlFor="primary-value">
                {orgProductIdentifier} for this product{" "}
                <span className="text-muted-foreground font-normal">
                  (optional)
                </span>
              </Label>
              <Input
                id="primary-value"
                placeholder={`Enter ${orgProductIdentifier} if known`}
                value={primaryValue}
                onChange={(e) => setPrimaryValue(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                If left blank, the product will be created with the extracted
                identifier and can be updated later.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              placeholder="Product description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating}>
              {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Product
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
